import viveApiConfig from './vive-api.config';
import {
  ViveApiConfigInterface,
  ViveApiRequestInfo
} from './vive-api.config.interface';
import { ViveApiHttpMethod, ViveApiHttpStatus } from './vive-api.enum';
import { ViveApiError } from './vive-api.error';

export { ViveApiError };

export declare type Association = {
  name: string;
  where?: { [key: string]: string | number | null | boolean };
  associations?: Association[];
};

export type ViveApiRequestParams = {
  apiEndpoint: string;
  options?: ViveApiRequestOptions;
  authRequired?: boolean;
  pathParams?: Record<string, string | number>;
  queryParams?: Record<string, unknown | null>;
  data?: unknown;
};

export type ViveApiRequestOptions = {
  includeAssociations?: Association[];
  requestTimeout?: number;
  additionalHeaders?: Record<string, string>;
  responseType?: 'blob';
  /**
   * Never sent and never read by the client: handed untouched to the config's
   * request hooks and its `makeApiClientRequest`, so a call can opt in or out
   * of what they do.
   */
  context?: Readonly<Record<string, unknown>>;
};

/**
 * Serializes an `Association[]` into the comma-joined form the API parses.
 *
 * Module scope rather than nested in the `options` branch, because the same
 * value can arrive through `queryParams` — the generated wrappers type it there
 * too — and both channels have to produce the same string.
 */
const buildIncludeParam = (associations: Association[]): string => {
  return associations
    .map((association) => {
      if (association.name.includes(',')) {
        throw new ViveApiError(
          `Association name ${association.name} must not contain commas`
        );
      }
      let where = '';
      if (association.where) {
        where = JSON.stringify(association.where);
      }
      if (association.associations?.length) {
        const nested = buildIncludeParam(association.associations);
        return `${association.name}${where}[${nested}]`;
      } else {
        return `${association.name}${where}`;
      }
    })
    .join(',');
};

/**
 * One query-string value.
 *
 * `String(value)` is correct for primitives, and deliberately correct for null
 * and undefined — the API reads those as the strings "null"/"undefined" and
 * callers rely on it. An array of primitives is also already right:
 * `String([1, 2])` is "1,2", which is the comma-joined form the API parses.
 *
 * It is silently wrong for anything holding an object. Those stringify to
 * "[object Object]" — a well-formed parameter that means nothing to the server,
 * so the request succeeds with the value dropped and nothing to indicate it.
 *
 * `includeAssociations` is the one such value with a defined wire format, and
 * the generated wrappers type it on `queryParams` as well as `options`, so both
 * channels must produce the same string. Everything else throws: there is no
 * correct encoding to guess at, and a failure at the call site is recoverable
 * in a way a dropped parameter in production is not.
 */
const serializeQueryParam = (key: string, value: unknown): string => {
  const isObject = (candidate: unknown): boolean =>
    typeof candidate === 'object' && candidate !== null;

  if (!isObject(value)) {
    return String(value);
  }

  if (Array.isArray(value)) {
    if (key === 'includeAssociations') {
      return buildIncludeParam(value as Association[]);
    }
    if (!value.some(isObject)) {
      return String(value);
    }
  }

  throw new ViveApiError(
    `sendRequestToViveAPI query param "${key}" holds ${
      Array.isArray(value) ? 'objects' : 'an object'
    } and has no defined query-string form. ` +
      `Pass associations via options.includeAssociations, or serialize this value at the call site.`
  );
};

/** A hook is the app's code: a bug in it must not fail the request it observes. */
const runRequestHook = (hook: () => void): void => {
  try {
    hook();
  } catch (error) {
    viveApiConfig.logErrorMessage(
      `vive-api request hook failed: ${
        error instanceof Error ? error.message : String(error)
      }`
    );
  }
};

export class ViveApiBaseService {
  protected static userToken: string | null = null;

  public static setUserToken(token: string | null): void {
    ViveApiBaseService.userToken = token;
  }

  protected async sendGet<T>(requestParams: ViveApiRequestParams): Promise<T> {
    return await this.send<T>({
      method: ViveApiHttpMethod.GET,
      ...requestParams
    });
  }

  protected async sendPost<T>(requestParams: ViveApiRequestParams): Promise<T> {
    return await this.send<T>({
      method: ViveApiHttpMethod.POST,
      ...requestParams
    });
  }

  protected async sendPut<T>(requestParams: ViveApiRequestParams): Promise<T> {
    return await this.send<T>({
      method: ViveApiHttpMethod.PUT,
      ...requestParams
    });
  }

  protected async sendPatch<T>(
    requestParams: ViveApiRequestParams
  ): Promise<T> {
    return await this.send<T>({
      method: ViveApiHttpMethod.PATCH,
      ...requestParams
    });
  }

  protected async sendDelete<T>(
    requestParams: ViveApiRequestParams
  ): Promise<T> {
    return await this.send<T>({
      method: ViveApiHttpMethod.DELETE,
      ...requestParams
    });
  }

  /**
   * One call as the config's request hooks see it: one start, one end. The
   * hooks wrap `sendRequest` rather than living in it because it recurses for
   * the retry after a 401, and that retry is part of the same call.
   */
  // TODO keep hooks for now until vive-app-v2 has been completely rebuilt.
  // Hooks aren't necessarily bad for certain use cases, but I would rather move from them.
  // Hooks are necessary in vive-app-v2 since it can't use HttpInterceptors with V3 fetch implementation.

  private async send<T>(
    requestParams: ViveApiRequestParams & { method: ViveApiHttpMethod }
  ): Promise<T> {
    // Typed through the interface: only a config that wants hooks declares
    // the member.
    const config: ViveApiConfigInterface = viveApiConfig;
    const hooks = config.requestHooks;
    if (!hooks) {
      return await this.sendRequest<T>(requestParams);
    }

    const request: ViveApiRequestInfo = {
      method: requestParams.method,
      endpoint: requestParams.apiEndpoint,
      context: requestParams.options?.context ?? {}
    };

    runRequestHook(() => hooks.onRequestStart?.(request));
    try {
      const result = await this.sendRequest<T>(requestParams);
      runRequestHook(() => hooks.onRequestEnd?.(request, { ok: true }));
      return result;
    } catch (error) {
      runRequestHook(() => hooks.onRequestEnd?.(request, { ok: false, error }));
      throw error;
    }
  }

  private async sendRequest<T>(
    requestParams: ViveApiRequestParams & {
      method: ViveApiHttpMethod;
      retryCount?: number;
    }
  ): Promise<T> {
    const startTime = new Date().getTime();

    const {
      method,
      retryCount = 1,

      authRequired = true,
      apiEndpoint,
      pathParams,
      queryParams,
      data,
      options = {}
    } = requestParams;

    const {
      includeAssociations,
      requestTimeout = 15000,
      additionalHeaders = {},
      responseType,
      context
    } = options;

    if (!apiEndpoint) {
      throw new ViveApiError('sendRequestToViveAPI missing endpoint param');
    }
    if (apiEndpoint.startsWith('/')) {
      throw new ViveApiError(
        `sendRequestToViveAPI endpoint param ${apiEndpoint} must not start with /`
      );
    }

    const baseUrl = viveApiConfig.getViveApiBaseUrl();
    const url = new URL(`${baseUrl}/api/v3/${apiEndpoint}`);
    if (pathParams) {
      const segments = url.pathname.split('/');
      const replacedSegments = segments.map((segment) => {
        if (segment.startsWith(':')) {
          const param = segment.slice(1);
          const pathParam = pathParams[param];
          if (pathParam !== undefined && pathParam !== null) {
            return encodeURIComponent(String(pathParam));
          } else {
            throw new ViveApiError(
              `sendRequestToViveAPI path param ${param} not defined`
            );
          }
        }
        return segment;
      });
      url.pathname = replacedSegments.join('/');
    }
    if (url.pathname.includes(':')) {
      throw new ViveApiError(
        `sendRequestToViveAPI endpoint has unreplaced pathParams ${url}`
      );
    }

    if (queryParams) {
      const keys = Object.keys(queryParams);
      if (keys.length) {
        keys.forEach((key) => {
          // Nulls and undefined still become the strings the API expects;
          // anything non-primitive is serialized properly or refused outright.
          url.searchParams.append(
            key,
            serializeQueryParam(key, queryParams[key])
          );
        });
      }
    }

    if (includeAssociations) {
      const includeParam = buildIncludeParam(includeAssociations);
      url.searchParams.append('includeAssociations', includeParam);
    }

    viveApiConfig.logDebugMessage(`vive-api request ${method} ${url}`);
    // Read with `in`: a Node consumer's config does not declare the member, and
    // without it the null-token throws below stay in force.
    const sessionMode =
      'sessionMode' in viveApiConfig && viveApiConfig.sessionMode === true;
    let responseStatus;
    try {
      let response: Response | null = null;
      try {
        // Null is normal in a browser session: the session cookie authenticates.
        if (authRequired && !ViveApiBaseService.userToken) {
          ViveApiBaseService.userToken = await viveApiConfig.getUserToken();
          if (!ViveApiBaseService.userToken && !sessionMode) {
            throw new ViveApiError(`Failed to obtain user token`);
          }
        }

        response = await viveApiConfig.makeApiClientRequest({
          url,
          method,
          requestTimeout,
          authRequired,
          userToken: ViveApiBaseService.userToken,
          additionalHeaders,
          data,
          context
        });
      } catch (error) {
        if (error instanceof Error && error.name === 'TimeoutError') {
          throw new ViveApiError(`vive-api request timed out`, {
            cause: 'Request timed out',
            httpStatusCode: ViveApiHttpStatus.GATEWAY_TIMEOUT
          });
        }
        if (error instanceof TypeError) {
          const errorMessage = error.message;
          const errorCauseCode =
            error.cause &&
            typeof error.cause === 'object' &&
            'code' in error.cause
              ? (error.cause as { code?: string }).code
              : undefined;

          if (
            errorMessage?.includes('ECONNREFUSED') ||
            errorCauseCode?.includes('ECONNREFUSED')
          ) {
            throw new ViveApiError(`vive-api unable to connect to server`, {
              cause: 'Connection refused',
              httpStatusCode: ViveApiHttpStatus.BAD_GATEWAY
            });
          } else {
            viveApiConfig.logErrorMessage(
              `Fetch failed due to unknown network error: ${error.message}`
            );
          }
        }
        throw error;
      }

      if (!response) {
        throw new ViveApiError(
          `No response from Vive API for ${method} ${url}`
        );
      }

      responseStatus = response.status;

      if (response.ok && responseType === 'blob') {
        return (await response.blob()) as T;
      }

      let responseData = null;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.indexOf('application/json') !== -1) {
        responseData = await response.json();
      } else {
        responseData = await response.text();
      }

      if (response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.indexOf('application/json') !== -1) {
          return responseData as T;
        }
        viveApiConfig.logDebugMessage('no content returned');
        return null as T;
      }

      // If the retry count is greater than 0, then retry IF
      // HttpStatus.UNAUTHORIZED indicates token expiration or revocation.
      // NestJS returns JSON 401s; legacy endpoints return plain string 'Unauthorized'.
      const isUnauthorizedResponse =
        typeof responseData === 'string'
          ? responseData.toLowerCase().includes('unauthorized')
          : typeof responseData === 'object' &&
            responseData !== null &&
            ((responseData as { statusCode?: number }).statusCode === 401 ||
              (responseData as { message?: string }).message
                ?.toLowerCase()
                .includes('unauthorized'));

      const config: ViveApiConfigInterface = viveApiConfig;
      if (
        !config.transportHandlesReauth &&
        authRequired &&
        retryCount > 0 &&
        (response.status as ViveApiHttpStatus) ===
          ViveApiHttpStatus.UNAUTHORIZED &&
        isUnauthorizedResponse
      ) {
        viveApiConfig.logDebugMessage(`TOKEN EXPIRED — triggering reauth`);
        // handleTokenExpired() deduplicates concurrent 401s via AuthReauthService._pendingReauth.
        // All in-flight requests await the same promise — only one modal opens.
        // Throws ReauthDismissedError if the user closes the modal without re-authenticating.
        const newToken = await viveApiConfig.handleTokenExpired();
        // Null after a browser-session reauth: the new session cookie carries the retry.
        ViveApiBaseService.setUserToken(newToken);

        if (ViveApiBaseService.userToken == null && !sessionMode) {
          throw new ViveApiError(
            'Token refresh failed — userToken is null after reauth'
          );
        }

        return await this.sendRequest<T>({
          ...requestParams,
          retryCount: retryCount - 1
        });
      }

      // ERROR case
      throw new ViveApiError(
        `vive-api fetch ${method} error [${response.status}]`,
        {
          cause: responseData,
          httpStatusCode: response.status
        }
      );
    } finally {
      let duration = new Date().getTime() - startTime;
      if (duration >= requestTimeout) {
        viveApiConfig.logErrorMessage(
          `vive-api request ${method} ${url} exceeded requestTimeout of ${requestTimeout}ms`
        );
        duration = requestTimeout; // Cap duration at requestTimeout for logging purposes
      }

      viveApiConfig.logMetrics({
        method,
        endpoint: apiEndpoint,
        responseStatus: responseStatus ?? -1,
        duration
      });
    }
  }
}
